import { Effect, Either } from "effect";
import { Temporal } from "@pima/utils";
import {
  type ClientLicenses,
  type DepartmentRow,
  type EmployeeRow,
  type EmployeeRoleKind,
  type EmployeeSort,
  type OperationalAreaRow,
  type UserLocationAssignment,
  type JobTitleRow,
} from "@pima/web-api/client";
import { runIntent, runIntentAsError, runIntentResult } from "../run-intent";
import { toAccountEmail } from "./account-email";
import { getClient } from "./client";

/**
 * Creates a draft employee of `clientId` with their directory fields and
 * locations in one request. A refusal throws an Error carrying its HTTP
 * `status` and the contract's tag as `cause`.
 */
export async function createEmployee(params: {
  clientId: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  language?: "de-DE" | "en-US";
  departmentId?: string;
  jobTitleId?: string;
  personalEmail?: string;
  personalNumber?: string;
  dateOfBirth?: string;
  locationIds?: readonly string[];
  additionalRoles?: string;
  emailNotifications?: boolean;
  publicRegistration?: boolean;
}): Promise<EmployeeRow> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client.employees.createEmployee({
      payload: {
        client_id: params.clientId,
        email: toAccountEmail(params.email),
        role: params.role,
        first_name: params.firstName,
        last_name: params.lastName,
        language: params.language,
        department_id: params.departmentId,
        job_title_id: params.jobTitleId,
        personal_email: params.personalEmail,
        personal_number: params.personalNumber,
        date_of_birth: params.dateOfBirth
          ? Temporal.PlainDate.from(params.dateOfBirth)
          : undefined,
        location_ids: params.locationIds,
        additional_roles: params.additionalRoles,
        email_notifications: params.emailNotifications,
        public_registration: params.publicRegistration,
      },
    }),
  );
  return data;
}

// employee-list's one read model — search + location-manager scoping
// happen server-side; the caller's own client is implicit via the
// principal, but `client_id` still rides the params (the intent allows an
// admin caller to pass another client's id, which the dashboard persona
// never exercises).
export async function fetchEmployees(params: {
  clientId: string;
  search?: string;
  locationIds?: string[];
  // The licenses view's own seats-usage table — narrows to the
  // ohs-officer/location-manager roles rather than the caller's location
  // assignment.
  roleIds?: string[];
  roleKinds?: EmployeeRoleKind[];
  // The employee-list "Sort by" control (PMO-19 #476/#523). Omitted keeps
  // the table's original insertion-order default.
  sort?: EmployeeSort;
  limit: number;
  offset: number;
}): Promise<{ items: readonly EmployeeRow[]; total: number }> {
  const client = await getClient();
  return runIntent(
    client.employees.listEmployees({
      urlParams: {
        client_id: params.clientId,
        limit: params.limit,
        offset: params.offset,
        ...(params.search ? { search: params.search } : {}),
        ...(params.locationIds?.length
          ? { location_ids: params.locationIds }
          : {}),
        ...(params.roleIds?.length ? { role_ids: params.roleIds } : {}),
        ...(params.roleKinds?.length ? { role_kinds: params.roleKinds } : {}),
        ...(params.sort ? { sort: params.sort } : {}),
      },
    }),
  );
}

const employeeCsvColumns: Array<keyof EmployeeRow> = [
  "user_first_name",
  "user_last_name",
  "user_email",
  "user_language",
  "user_status",
  "user_role_name",
  "user_additional_roles",
  "user_public_registration",
  "job_title_name",
  "department_name",
  "personal_email",
  "personal_number",
  "date_of_birth",
  "date_created",
  "unique_key",
  "location_names",
];

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? "" : String(value);
  return `"${text.replaceAll('"', '""')}"`;
};

export async function exportEmployeesCsv(params: {
  clientId: string;
  locationIds?: string[];
}): Promise<string> {
  const rows: EmployeeRow[] = [];
  let offset = 0;
  let total = 0;
  do {
    const page = await fetchEmployees({
      clientId: params.clientId,
      locationIds: params.locationIds,
      limit: 100,
      offset,
    });
    rows.push(...page.items);
    total = page.total;
    offset += page.items.length;
  } while (offset < total);

  return [
    employeeCsvColumns.map(csvCell).join(","),
    ...rows.map((row) =>
      employeeCsvColumns.map((column) => csvCell(row[column])).join(","),
    ),
  ].join("\n");
}

export async function fetchUserLocations(params: {
  userId: string;
  clientId?: string;
}): Promise<readonly UserLocationAssignment[]> {
  const client = await getClient();
  const { items } = await Effect.runPromise(
    client.employees.listUserLocations({
      urlParams: {
        user_id: params.userId,
        ...(params.clientId ? { client_id: params.clientId } : {}),
      },
    }),
  );
  return items;
}

export async function fetchUserIdsByLocations(params: {
  clientId: string;
  locationIds: string[];
}): Promise<readonly string[]> {
  const client = await getClient();
  const { user_ids } = await Effect.runPromise(
    client.employees.listUsersByLocations({
      payload: {
        client_id: params.clientId,
        location_ids: params.locationIds,
      },
    }),
  );
  return user_ids;
}

export async function fetchEmployeeLocations(params: {
  clientId: string;
  ids?: readonly string[];
}): Promise<readonly import("@pima/web-api/client").LocationRow[]> {
  const client = await getClient();
  const { items } = await runIntent(
    client.employees.listEmployeeLocations({
      payload: {
        client_id: params.clientId,
        ...(params.ids !== undefined ? { ids: [...params.ids] } : {}),
      },
    }),
  );
  return items;
}

export async function fetchDepartments(params: {
  clientId: string;
  ids?: readonly string[];
}): Promise<readonly DepartmentRow[]> {
  const client = await getClient();
  const { items } = await runIntent(
    client.employees.listDepartments({
      payload: {
        client_id: params.clientId,
        ...(params.ids !== undefined ? { ids: [...params.ids] } : {}),
      },
    }),
  );
  return items;
}

export async function fetchJobTitles(params: {
  clientId: string;
  ids?: readonly string[];
}): Promise<readonly JobTitleRow[]> {
  const client = await getClient();
  const { items } = await runIntent(
    client.employees.listJobTitles({
      payload: {
        client_id: params.clientId,
        ...(params.ids !== undefined ? { ids: [...params.ids] } : {}),
      },
    }),
  );
  return items;
}

export async function fetchOperationalAreas(params: {
  clientId: string;
  ids?: readonly string[];
}): Promise<readonly OperationalAreaRow[]> {
  const client = await getClient();
  const { items } = await runIntent(
    client.employees.listOperationalAreas({
      payload: {
        client_id: params.clientId,
        ...(params.ids !== undefined ? { ids: [...params.ids] } : {}),
      },
    }),
  );
  return items;
}

// The upsert-employee modal's inline "create new" affordance on the
// department/job-title pickers.
export async function createDepartment(params: {
  clientId: string;
  name: string;
}): Promise<DepartmentRow> {
  const client = await getClient();
  const { data } = await runIntent(
    client.employees.createDepartment({
      payload: { client_id: params.clientId, name: params.name },
    }),
  );
  return data;
}

export async function createJobTitle(params: {
  clientId: string;
  name: string;
}): Promise<JobTitleRow> {
  const client = await getClient();
  const { data } = await runIntent(
    client.employees.createJobTitle({
      payload: { client_id: params.clientId, name: params.name },
    }),
  );
  return data;
}

export async function generateDummyEmail(params: {
  firstName: string;
  domain: string;
}): Promise<string> {
  const client = await getClient();
  const { data } = await Effect.runPromise(
    client.employees.generateDummyEmail({
      payload: { first_name: params.firstName, domain: params.domain },
    }),
  );
  return data.email;
}

export async function validateEmployeeUpload(params: {
  clientId: string;
  emails: string[];
}) {
  const emails = [
    ...new Set(
      params.emails
        .map((email) => email.trim().toLowerCase())
        .filter((email) => email.length > 0),
    ),
  ];
  const client = await getClient();
  const result = await runIntentResult(
    client.employees.validateEmployeeUpload({
      payload: { client_id: params.clientId, emails },
    }),
  );
  return Either.map(result, ({ data }) => data);
}

/**
 * Invites a member of `clientId`, or re-sends the invitation to a draft,
 * invited or unverified one. `invited` is false for an account left alone
 * (active, archived or suspended, or another client's); `userId` is null for
 * another client's account.
 */
export async function inviteEmployee(params: {
  clientId: string;
  userId?: string;
  email: string;
  role: string;
  inviteUrl?: string;
  firstName?: string;
  lastName?: string;
  language?: "de-DE" | "en-US";
  additionalRoles?: string;
  emailNotifications?: boolean;
  departmentId?: string;
  jobTitleId?: string;
  personalEmail?: string;
  personalNumber?: string;
  dateOfBirth?: string;
  locationIds?: readonly string[];
}): Promise<{ userId: string | null; invited: boolean }> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client.employees.inviteEmployee({
      payload: {
        client_id: params.clientId,
        ...(params.userId !== undefined ? { user_id: params.userId } : {}),
        email: toAccountEmail(params.email),
        role: params.role,
        ...(params.inviteUrl !== undefined
          ? { invite_url: params.inviteUrl }
          : {}),
        ...(params.firstName !== undefined
          ? { first_name: params.firstName }
          : {}),
        ...(params.lastName !== undefined
          ? { last_name: params.lastName }
          : {}),
        ...(params.language !== undefined ? { language: params.language } : {}),
        ...(params.additionalRoles !== undefined
          ? { additional_roles: params.additionalRoles }
          : {}),
        ...(params.emailNotifications !== undefined
          ? { email_notifications: params.emailNotifications }
          : {}),
        department_id: params.departmentId,
        job_title_id: params.jobTitleId,
        personal_email: params.personalEmail,
        personal_number: params.personalNumber,
        date_of_birth: params.dateOfBirth
          ? Temporal.PlainDate.from(params.dateOfBirth)
          : undefined,
        ...(params.locationIds !== undefined
          ? { location_ids: params.locationIds }
          : {}),
      },
    }),
  );
  return { userId: data.user_id, invited: data.invited };
}

export async function updateEmployee(params: {
  userId: string;
  clientId: string;
  email?: string;
  role?: string;
  emailNotifications?: boolean;
  firstName?: string;
  lastName?: string;
  language?: "de-DE" | "en-US";
  status?: "draft" | "invited" | "active" | "suspended" | "archived";
  departmentId?: string | null;
  jobTitleId?: string | null;
  personalEmail?: string | null;
  personalNumber?: string | null;
  dateOfBirth?: string | null;
  locationIds?: readonly string[];
  additionalRoles?: string | null;
}): Promise<EmployeeRow> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client.employees.updateEmployee({
      payload: {
        user_id: params.userId,
        client_id: params.clientId,
        email:
          params.email !== undefined ? toAccountEmail(params.email) : undefined,
        role: params.role,
        email_notifications: params.emailNotifications,
        ...(params.firstName !== undefined
          ? { first_name: params.firstName }
          : {}),
        ...(params.lastName !== undefined
          ? { last_name: params.lastName }
          : {}),
        ...(params.language !== undefined ? { language: params.language } : {}),
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.departmentId !== undefined
          ? { department_id: params.departmentId }
          : {}),
        ...(params.jobTitleId !== undefined
          ? { job_title_id: params.jobTitleId }
          : {}),
        ...(params.personalEmail !== undefined
          ? { personal_email: params.personalEmail }
          : {}),
        ...(params.personalNumber !== undefined
          ? { personal_number: params.personalNumber }
          : {}),
        ...(params.dateOfBirth !== undefined
          ? {
              date_of_birth:
                params.dateOfBirth === null
                  ? null
                  : Temporal.PlainDate.from(params.dateOfBirth),
            }
          : {}),
        ...(params.locationIds !== undefined
          ? { location_ids: [...params.locationIds] }
          : {}),
        ...(params.additionalRoles !== undefined
          ? { additional_roles: params.additionalRoles }
          : {}),
      },
    }),
  );
  return data;
}

export async function deleteEmployee(params: { id: string }): Promise<void> {
  const client = await getClient();
  await runIntent(
    client.employees.deleteEmployee({ payload: { id: params.id } }),
  );
}

/**
 * The create-group modal's server-side AND/OR matching engine.
 * `filter_operator` combines department, job-title, role, and location
 * criteria; direct employee picks always ride along unioned.
 */
export async function previewGroupMatches(params: {
  clientId: string;
  filterOperator: "and" | "or";
  locationIds?: readonly string[];
  departmentIds?: readonly string[];
  jobTitleIds?: readonly string[];
  roleIds?: readonly string[];
  employeeIds?: readonly string[];
}): Promise<readonly string[]> {
  const client = await getClient();
  const { user_ids } = await runIntent(
    client.employees.previewGroupMatches({
      payload: {
        client_id: params.clientId,
        filter_operator: params.filterOperator,
        ...(params.locationIds?.length
          ? { location_ids: [...params.locationIds] }
          : {}),
        ...(params.departmentIds?.length
          ? { department_ids: [...params.departmentIds] }
          : {}),
        ...(params.jobTitleIds?.length
          ? { job_title_ids: [...params.jobTitleIds] }
          : {}),
        ...(params.roleIds?.length ? { role_ids: [...params.roleIds] } : {}),
        ...(params.employeeIds?.length
          ? { employee_ids: [...params.employeeIds] }
          : {}),
      },
    }),
  );
  return user_ids;
}

export async function createOperationalArea(params: {
  clientId: string;
  name: string;
  userIds: readonly string[];
  filterOperator: "and" | "or";
  filter?: unknown;
}): Promise<OperationalAreaRow> {
  const client = await getClient();
  const { data } = await runIntent(
    client.employees.createOperationalArea({
      payload: {
        client_id: params.clientId,
        name: params.name,
        user_ids: [...params.userIds],
        filter_operator: params.filterOperator,
        ...(params.filter !== undefined ? { filter: params.filter } : {}),
      },
    }),
  );
  return data;
}

export async function updateOperationalArea(params: {
  id: string;
  clientId: string;
  name?: string;
  userIds?: readonly string[];
  filterOperator?: "and" | "or";
  filter?: unknown;
}): Promise<OperationalAreaRow> {
  const client = await getClient();
  const { data } = await runIntent(
    client.employees.updateOperationalArea({
      payload: {
        id: params.id,
        client_id: params.clientId,
        ...(params.name !== undefined ? { name: params.name } : {}),
        ...(params.userIds !== undefined
          ? { user_ids: [...params.userIds] }
          : {}),
        ...(params.filterOperator !== undefined
          ? { filter_operator: params.filterOperator }
          : {}),
        ...(params.filter !== undefined ? { filter: params.filter } : {}),
      },
    }),
  );
  return data;
}

export async function deleteOperationalArea(params: {
  id: string;
  clientId: string;
}): Promise<void> {
  const client = await getClient();
  await runIntent(
    client.employees.deleteOperationalArea({
      payload: { id: params.id, client_id: params.clientId },
    }),
  );
}

// One request supplies the license view's seat usage and the
// upsert-employee modal's pre-flight `checkClientLicenses` check.
export async function fetchClientLicenses(params: {
  clientId: string;
}): Promise<ClientLicenses> {
  const client = await getClient();
  const { data } = await runIntent(
    client.employees.getClientLicenses({
      urlParams: {
        client_id: params.clientId,
      },
    }),
  );
  return data;
}

/** Downloads an upload's failed rows with the caller's current session. */
export async function downloadEmployeeUploadFailedRows(uploadId: string) {
  const client = await getClient();
  return runIntentResult(
    client.employees.downloadEmployeeUploadFailedRows({
      path: { upload_id: uploadId },
    }),
  );
}

export async function queueEmployeeUpload(
  payload: import("@pima/web-api/client").QueueEmployeeUploadPayload,
) {
  const client = await getClient();
  return runIntentResult(client.employees.queueEmployeeUpload({ payload }));
}

export async function fetchAdditionalRoles(input: {
  clientId: string;
  search: string;
  limit: number;
}): Promise<string[]> {
  const client = await getClient();
  const { data } = await runIntentAsError(
    client.employees.listAdditionalRoles({
      payload: {
        client_id: input.clientId,
        search: input.search,
        limit: input.limit,
      },
    }),
  );
  return [...data];
}
export async function fetchEmployeeInvitationLink(
  userId: string,
  url: string | null,
): Promise<{ url: string }> {
  const client = await getClient();
  return runIntentAsError(
    client.employees.getEmployeeInvitationLink({
      payload: { user_id: userId, url },
    }),
  );
}
